import { useEffect, useRef } from 'react';
import { Button } from '../components/Button.jsx';
import { DisasterCard } from '../components/DisasterCard.jsx';
import { useFocusOnMount } from '../components/useFocusOnMount.js';
import { CostBreakdown } from '../components/CostBreakdown.jsx';
import { advanceYear } from '../state/actions.js';
import { lastYearResult, isAdvancedMode } from '../state/selectors.js';
import { configForState } from '../state/modeConfig.js';
import { GAME_YEARS } from '../data/constants.js';
import { PLANT_TYPE } from '../data/plants.js';
import { FAMILY_LABEL } from '../data/families.js';
import {
  formatTwh,
  formatGw,
  formatPercent
} from '../components/format.js';
import {
  affectedPlantIds,
  disasterImpactsPlayer
} from '../state/disasterImpact.js';
import { EFFECT_KIND } from '../data/disasters.js';
import {
  computeDisasterEffects,
  computeFinalOutputPerType,
  demandAddAmountForEffect,
  outputMultiplierFactorForPlant
} from '../engine/disasters.js';
import {
  isStoragePlant,
  baseFirmCapacityUnits,
  firmFactorFromGenerationFactor,
  computeAdvancedFirmCapacityTotalByType
} from '../engine/advancedCapacity.js';
import { ADVANCED_DISASTER_EFFECTS } from '../data/advancedDisasters.js';
import { useAudio } from '../audio/AudioProvider.jsx';

function expectedLabelFor(plant, count) {
  if (plant.type === PLANT_TYPE.RELIABLE) {
    return formatTwh(plant.output * count);
  }
  return `${plant.output.min * count} to ${plant.output.max * count} TWh`;
}

// Header stat. Colour is reserved for the one cell that costs money;
// every other cell stays neutral so the eye lands on the exception.
function ReportStat({ label, value, note = null, risk = false }) {
  return (
    <div className="grid-report-stat">
      <dt className="grid-report-stat-label">{label}</dt>
      <dd
        className={
          'grid-report-stat-value' +
          (risk ? ' grid-report-stat-value-risk' : '')
        }
      >
        {value}
      </dd>
      {note ? <dd className="grid-report-stat-note">{note}</dd> : null}
    </div>
  );
}

function expectedMeanFor(plant, count) {
  if (plant.type === PLANT_TYPE.RELIABLE) {
    return plant.output * count;
  }
  return ((plant.output.min + plant.output.max) / 2) * count;
}

function actualColourFor(actual, mean) {
  const diff = actual - mean;
  if (Math.abs(diff) < 0.0001) return 'white';
  return diff > 0 ? '#22c55e' : '#ef4444';
}

export function reportCueForResult(result, advanced = false) {
  if (!result) return null;
  const hasShortfall =
    result.shortfall > 0 || (advanced && result.capacityShortfall > 0);
  if (hasShortfall) return 'shortfallConfirmed';
  if (result.totalOutput > result.demand) return 'surplusSecured';
  return null;
}

function describeOutputMultiplier(disaster, plantId) {
  const f = outputMultiplierFactorForPlant(disaster.effect, plantId);
  if (f === 0) return 'took it offline';
  const pct = Math.round((1 - f) * 100);
  return `cut output by ${pct}%`;
}

function severityLabel(disaster) {
  if (disaster.severityRoll === 'mild') return 'Mild';
  if (disaster.severityRoll === 'severe') return 'Severe';
  return null;
}

function disasterNameWithSeverity(disaster) {
  const severity = severityLabel(disaster);
  return severity ? `${disaster.name} (${severity})` : disaster.name;
}

function resolvedAffectedPlantIds(
  disaster,
  portfolio,
  plantsById,
  affectedPlantIdsByDisasterId
) {
  return (
    affectedPlantIdsByDisasterId?.[disaster.id] ??
    affectedPlantIds(disaster, portfolio, plantsById)
  );
}

function buildExplainers(
  plant,
  disasters,
  portfolio,
  plantsById,
  affectedPlantIdsByDisasterId
) {
  const lines = [];

  for (const d of disasters) {
    if (d.effect.kind !== 'output-multiplier') continue;
    const affected = resolvedAffectedPlantIds(
      d,
      portfolio,
      plantsById,
      affectedPlantIdsByDisasterId
    );
    if (!affected.includes(plant.id)) continue;
    lines.push(
      `${disasterNameWithSeverity(d)} ${describeOutputMultiplier(d, plant.id)}`
    );
  }

  return lines;
}

function describeFirmFactor(factor) {
  if (factor === 0) return 'took firm capacity offline';
  return `cut firm capacity by ${Math.round((1 - factor) * 100)}%`;
}

// Advanced Capacity Mode explainers: one line per disaster covering
// both channels — the annual generation hit and the (softened or
// derated) firm capacity hit (section 16.6).
function buildAdvancedExplainers(
  plant,
  disasters,
  portfolio,
  plantsById,
  affectedPlantIdsByDisasterId
) {
  const lines = [];

  for (const d of disasters) {
    const adv = d.advanced ?? ADVANCED_DISASTER_EFFECTS[d.id] ?? {};
    const parts = [];

    if (d.effect.kind === EFFECT_KIND.OUTPUT_MULTIPLIER) {
      const affected = resolvedAffectedPlantIds(
        d,
        portfolio,
        plantsById,
        affectedPlantIdsByDisasterId
      );
      if (affected.includes(plant.id)) {
        const generationFactor = outputMultiplierFactorForPlant(
          d.effect,
          plant.id
        );
        if (
          adv.affectsGeneration !== false &&
          !isStoragePlant(plant) &&
          generationFactor !== 1
        ) {
          parts.push(describeOutputMultiplier(d, plant.id));
        }
        if (adv.affectsFirmCapacity !== false) {
          const firmFactor = adv.firmUsesGenerationFactor
            ? generationFactor
            : adv.firmCapacityFactor ??
              firmFactorFromGenerationFactor(generationFactor);
          if (firmFactor !== 1) parts.push(describeFirmFactor(firmFactor));
        }
      }
    }

    // Firm-capacity-only derates hit every owned plant in the named
    // families, independent of the Classic target.
    if (adv.firmDerate) {
      const derates = Array.isArray(adv.firmDerate)
        ? adv.firmDerate
        : [adv.firmDerate];
      for (const derate of derates) {
        if ((derate.families ?? []).includes(plant.family)) {
          parts.push(describeFirmFactor(derate.factor));
        }
      }
    }

    if (parts.length > 0) {
      lines.push(`${disasterNameWithSeverity(d)} ${parts.join(' and ')}`);
    }
  }

  return lines;
}

function PlantPerformanceCard({
  plant,
  count,
  expected,
  actual,
  mean,
  explainers,
  formatActual = formatTwh,
  actualDisplay,
  expectedFirm,
  actualFirm,
  expectedFirmDisplay,
  actualFirmDisplay,
  testId
}) {
  const energyColour = actualColourFor(actual, mean);
  const firmColour =
    expectedFirm === undefined || actualFirm === undefined
      ? 'white'
      : actualColourFor(actualFirm, expectedFirm);
  const hasFirmAccounting =
    expectedFirmDisplay !== undefined && actualFirmDisplay !== undefined;
  const isLow = energyColour === '#ef4444' || firmColour === '#ef4444';
  return (
    <article
      className={
        `grid-report-plant grid-report-plant-${plant.family}` +
        (isLow ? ' grid-report-plant-low' : '')
      }
      data-testid={testId}
    >
      <header className="grid-report-plant-header">
        <span className="grid-report-plant-family">
          {FAMILY_LABEL[plant.family]}
        </span>
        <span
          className={`grid-report-plant-type grid-report-plant-type-${plant.type}`}
        >
          {isStoragePlant(plant)
            ? 'Storage'
            : plant.type === PLANT_TYPE.VARIABLE
              ? 'Variable'
              : 'Reliable'}
        </span>
      </header>
      <img
        className="plant-tile-image"
        src={plant.imageSrc ?? `/plant-images/${plant.id}.webp`}
        alt=""
        loading="eager"
        decoding="async"
        onError={(e) => {
          e.currentTarget.style.display = 'none';
        }}
      />
      <h3 className="grid-report-plant-name">
        {plant.name}
        {count > 1 ? (
          <span
            style={{
              marginLeft: '0.5rem',
              fontWeight: 700,
              fontSize: '1.1em',
              color: '#fbbf24'
            }}
          >
            x{count}
          </span>
        ) : null}
      </h3>
      <dl className="grid-report-plant-stats">
        <div>
          <dt>Expected</dt>
          <dd className="grid-report-plant-expected">
            {hasFirmAccounting ? (
              <>
                <span className="grid-report-plant-metric">
                  <span className="grid-report-plant-metric-label">Energy</span>
                  <span>{expected}</span>
                </span>
                <span className="grid-report-plant-metric">
                  <span className="grid-report-plant-metric-label">
                    Firm capacity
                  </span>
                  <span>{expectedFirmDisplay}</span>
                </span>
              </>
            ) : (
              expected
            )}
          </dd>
        </div>
        <div>
          <dt>Actual</dt>
          <dd className="grid-report-plant-actual">
            {hasFirmAccounting ? (
              <>
                <span className="grid-report-plant-metric">
                  <span className="grid-report-plant-metric-label">Energy</span>
                  <span style={{ color: energyColour }}>
                    {actualDisplay ?? formatActual(actual)}
                  </span>
                </span>
                <span className="grid-report-plant-metric">
                  <span className="grid-report-plant-metric-label">
                    Firm capacity
                  </span>
                  <span style={{ color: firmColour }}>
                    {actualFirmDisplay}
                  </span>
                </span>
              </>
            ) : (
              <span style={{ color: energyColour }}>
                {actualDisplay ?? formatActual(actual)}
              </span>
            )}
          </dd>
        </div>
      </dl>
      {explainers.length > 0 ? (
        <ul className="grid-report-plant-explainers" role="list">
          {explainers.map((line, i) => (
            <li key={i} className="grid-report-plant-explainer">
              {line}
            </li>
          ))}
        </ul>
      ) : null}
    </article>
  );
}

export function GridPerformanceReportScreen({ state, dispatch }) {
  const mainRef = useFocusOnMount();
  const introRef = useRef(null);
  const reportCuePlayedRef = useRef(false);
  const result = lastYearResult(state);
  const isLastYear = state.year >= GAME_YEARS;
  const isAdvanced = isAdvancedMode(state);
  const { playCue } = useAudio();

  useEffect(() => {
    if (!introRef.current) return;
    const node = introRef.current;
    requestAnimationFrame(() => {
      node.classList.add('grid-report-intro-shown');
    });
  }, []);

  useEffect(() => {
    if (!result || reportCuePlayedRef.current) return undefined;

    const cueName = reportCueForResult(result, isAdvanced);
    if (!cueName) return undefined;
    const timer = window.setTimeout(() => {
      reportCuePlayedRef.current = true;
      playCue(cueName);
    }, 1000);

    return () => window.clearTimeout(timer);
  }, [isAdvanced, playCue, result]);

  if (!result) {
    return null;
  }

  const config = configForState(state);
  const plantsById = config.plantsById;
  const portfolio = state.portfolio;
  const disasters = result.disasters ?? [];
  const subsidyReformWasDrawn = state.drawnDisasterIds.includes('subsidy-reform');
  const displayDisasters =
    subsidyReformWasDrawn && !disasters.some((d) => d.id === 'subsidy-reform')
      ? [...disasters, config.disasterById['subsidy-reform']]
      : disasters;
  const rolledOutputs = result.rolledOutputs ?? {};
  const affectedPlantIdsByDisasterId =
    result.affectedPlantIdsByDisasterId ?? {};
  let outputPerType;
  if (isAdvanced) {
    // Advanced year results carry the resolved per-type generation
    // (storage-aware, separate firm-capacity channel).
    outputPerType = result.generationPerType ?? {};
  } else {
    outputPerType = result.outputPerType;
    if (!outputPerType) {
      const { outputMultipliers } = computeDisasterEffects({
        disasters,
        portfolio,
        plantsById,
        year: state.year
      });
      outputPerType = computeFinalOutputPerType({
        portfolio,
        plantsById,
        rolledOutputs,
        outputMultipliers
      });
    }
  }

  const groupedOrder = [];
  const groupedCounts = {};
  for (const id of portfolio) {
    if (groupedCounts[id] === undefined) {
      groupedOrder.push(id);
      groupedCounts[id] = 0;
    }
    groupedCounts[id] += 1;
  }

  const expectedFirmCapacityByType = isAdvanced
    ? computeAdvancedFirmCapacityTotalByType({
        portfolio,
        plantsById,
        firmCapacityMultipliers: {},
        chargeLevels: result.storageChargeLevels ?? {},
        diversityUplift: result.diversityUplift ?? 0
      })
    : {};

  // Storage's share of firm capacity. Peak capacity is the only
  // demand layer storage answers directly, and the per-plant list
  // alone never shows how much of the headline total it carried.
  // firmCapacityTotalByType is already totalled across every copy, so
  // multiplying by the count here would double-count the group.
  const storageFirmCapacity = groupedOrder.reduce(
    (s, id) =>
      isStoragePlant(plantsById[id])
        ? s + (result.firmCapacityTotalByType?.[id] ?? 0)
        : s,
    0
  );
  const carriedForwardTotal = Object.values(
    result.carriedChargeForNextYear ?? {}
  ).reduce((s, v) => s + v, 0);

  const opexDisasters = disasters.filter(
    (d) => d.effect.kind === EFFECT_KIND.OPEX_MULTIPLIER
  );
  const opexNote =
    opexDisasters.length > 0
      ? `(${opexDisasters
          .map((d) => `${d.name}: opex increases to ${Math.round(d.effect.factor * 100)}%`)
          .join('; ')})`
      : null;

  const demandAddDisasters = disasters.filter(
    (d) => d.effect.kind === EFFECT_KIND.DEMAND_ADD
  );
  const demandAddTotal = isAdvanced
    ? (result.energyDemandAdd ??
      demandAddDisasters.reduce(
        (s, d) => s + demandAddAmountForEffect(d.effect, state.year),
        0
      ))
    : (result.demandAdd ??
      demandAddDisasters.reduce(
        (s, d) => s + demandAddAmountForEffect(d.effect, state.year),
        0
      ));
  const peakDemandAdd = isAdvanced ? (result.peakDemandAdd ?? 0) : 0;
  const disasterImpactContext = {
    portfolio,
    plantsById,
    subsidyActive: result.subsidyActive || result.subsidyReformed
  };
  const disasterImpactById = Object.fromEntries(
    displayDisasters.map((d) => [
      d.id,
      d.id === 'subsidy-reform' && subsidyReformWasDrawn
        ? true
        : disasterImpactsPlayer(d, disasterImpactContext)
    ])
  );

  const retiredNames = (result.retiredThisYear ?? []).map(
    (id) => plantsById[id]?.name ?? id
  );
  const rows = [
    {
      label: isAdvanced
        ? 'Listed capital spent on new plants this year'
        : 'Capital spent on new plants this year',
      amount: result.capital
    },
    ...((result.resourceDevelopmentPremium ?? 0) > 0
      ? [
          {
            label: 'Resource development premium',
            amount: result.resourceDevelopmentPremium
          }
        ]
      : []),
    {
      label: 'Operating cost',
      amount: result.opex,
      tone: opexNote ? 'warning' : null,
      note: opexNote
    },
    ...((result.decommissionCost ?? 0) > 0
      ? [
          {
            label: 'Decommissioning fees',
            amount: result.decommissionCost,
            note: `(retired ${retiredNames.join(', ')})`
          }
        ]
      : []),
    {
      label: isAdvanced ? 'Energy shortfall penalty' : 'Shortfall penalty',
      amount: result.shortfallCost,
      tone: result.shortfallCost > 0 ? 'warning' : null,
      note:
        result.shortfall > 0
          ? `(${formatTwh(result.shortfall)} short of ${formatTwh(result.demand)})`
          : null
    }
  ];
  if (isAdvanced) {
    rows.push({
      label: 'Peak capacity penalty',
      amount: result.capacityShortfallCost,
      tone: result.capacityShortfallCost > 0 ? 'warning' : null,
      note:
        result.capacityShortfall > 0
          ? `(${formatGw(result.capacityShortfall)} short of ${formatGw(result.requiredFirmCapacityUnits)} required firm)`
          : null
    });
  }

  const yearTotal =
    result.capital +
    (result.resourceDevelopmentPremium ?? 0) +
    result.opex +
    (result.decommissionCost ?? 0) +
    result.shortfallCost +
    (result.capacityShortfallCost ?? 0);

  const continueLabel = isLastYear
    ? 'Continue to COP 31 Antalya outcome'
    : `Begin Year ${state.year + 1} build`;

  function handleAdvanceYear() {
    dispatch(advanceYear());
  }

  return (
    <main
      ref={mainRef}
      id="main"
      tabIndex={-1}
      className="screen screen-grid-report"
    >
      <div ref={introRef} className="grid-report-intro">
        <header className="grid-report-header">
          <div className="grid-report-header-left">
            <div className="grid-report-eyebrow">
              Year {state.year} complete
            </div>
            <h2 className="grid-report-title" id="grid-report-heading">
              Grid Performance Report
            </h2>
            <dl className="grid-report-stats-strip">
              <ReportStat
                label={`Plant${portfolio.length === 1 ? '' : 's'}`}
                value={portfolio.length}
              />
              <ReportStat
                label="Demand"
                value={formatTwh(result.demand)}
                note={
                  demandAddTotal > 0
                    ? `+${demandAddTotal} from ${demandAddDisasters
                        .map((d) => d.name)
                        .join(', ')}`
                    : null
                }
              />
              <ReportStat
                label="Delivered"
                value={formatTwh(result.totalOutput)}
              />
              {result.totalOutput >= result.demand ? (
                <ReportStat
                  label="Surplus"
                  value={formatTwh(result.totalOutput - result.demand)}
                />
              ) : (
                <ReportStat
                  label="Shortfall"
                  value={formatTwh(result.demand - result.totalOutput)}
                  risk
                />
              )}
            </dl>
            {isAdvanced ? (
              <dl
                className="grid-report-stats-strip"
                data-testid="grid-report-capacity-line"
              >
                <ReportStat
                  label="Peak demand"
                  value={formatGw(result.peakDemandCapacityUnits)}
                  note={
                    peakDemandAdd > 0
                      ? `+${formatGw(peakDemandAdd)} from ${demandAddDisasters
                          .map((d) => d.name)
                          .join(', ')}`
                      : null
                  }
                />
                <ReportStat
                  label="Required firm (x1.1)"
                  value={formatGw(result.requiredFirmCapacityUnits)}
                  note={`${formatGw(result.peakDemandCapacityUnits)} x 1.1 reserve`}
                />
                <ReportStat
                  label="Firm capacity"
                  value={formatGw(result.actualFirmCapacityUnits)}
                  note={
                    storageFirmCapacity > 0
                      ? `incl. ${formatGw(storageFirmCapacity)} from storage`
                      : null
                  }
                />
                {result.capacityShortfall > 0 ? (
                  <ReportStat
                    label="Capacity shortfall"
                    value={formatGw(result.capacityShortfall)}
                    risk
                  />
                ) : (
                  <ReportStat
                    label="Capacity"
                    value="Adequate"
                  />
                )}
              </dl>
            ) : null}
          </div>
          <div className="grid-report-year">
            {isLastYear ? (
              <>
                Closing the game at <strong>Year {state.year}</strong>
              </>
            ) : (
              <>
                Advancing to <strong>Year {state.year + 1}</strong>
              </>
            )}
          </div>
        </header>

        <section
          className="grid-report-section"
          aria-labelledby="grid-report-cost-heading"
        >
          <h3
            id="grid-report-cost-heading"
            className="grid-report-section-heading"
          >
            Year {state.year} cost
          </h3>
          <CostBreakdown rows={rows} total={yearTotal} totalLabel="Year cost" />
          <p className="grid-report-emissions">
            Emissions this year: {formatEmissionsLabel(result.emissions)}
          </p>
          {isAdvanced && !isLastYear && carriedForwardTotal > 0 ? (
            <p
              className="grid-report-emissions"
              data-testid="grid-report-storage-carry"
            >
              Storage carries {formatTwh(carriedForwardTotal)} of charge
              into next year (after round-trip losses). It arrives already
              charged, so it contributes firm capacity next year without
              drawing on that year's surplus.
            </p>
          ) : null}
        </section>

        <section
          className="grid-report-section grid-report-section-major"
          aria-labelledby="grid-report-plants-heading"
        >
          <h3
            id="grid-report-plants-heading"
            className="grid-report-section-heading"
          >
            Plant performance
          </h3>
          <ul className="grid-report-plant-list" role="list">
            {groupedOrder.map((plantId) => {
              const plant = plantsById[plantId];
              const count = groupedCounts[plantId];
              if (isStoragePlant(plant)) {
                const firmTotal =
                  result.firmCapacityTotalByType?.[plantId] ?? 0;
                const expectedFirmTotal =
                  expectedFirmCapacityByType[plantId] ?? 0;
                // Rated firm is the ceiling a full charge can reach.
                // Without it the charge percentage reads as the whole
                // story, hiding how small the ceiling itself is.
                const ratedFirm = baseFirmCapacityUnits(plant) * count;
                const chargePct = formatPercent(
                  result.storageChargeLevels?.[plantId] ?? 0
                );
                return (
                  <li key={plantId}>
                    <PlantPerformanceCard
                      plant={plant}
                      count={count}
                      expected="N/A"
                      actual={0}
                      mean={0}
                      actualDisplay="N/A"
                      expectedFirm={expectedFirmTotal}
                      actualFirm={firmTotal}
                      expectedFirmDisplay={formatGw(expectedFirmTotal)}
                      actualFirmDisplay={formatGw(firmTotal)}
                      explainers={[
                        `${chargePct} charged from expected surplus against ${formatGw(ratedFirm)} rated firm capacity`,
                        ...((result.storageChargeLevels?.[plantId] ?? 0) <= 0
                          ? ['No expected surplus to charge from; contributed no firm capacity']
                          : []),
                        ...buildAdvancedExplainers(
                          plant,
                          disasters,
                          portfolio,
                          plantsById,
                          affectedPlantIdsByDisasterId
                        )
                      ]}
                      testId={`grid-report-plant-${plantId}`}
                    />
                  </li>
                );
              }
              const perUnitActual = outputPerType[plantId] ?? 0;
              const actualSum = perUnitActual * count;
              const expected = expectedLabelFor(plant, count);
              const mean = expectedMeanFor(plant, count);
              const explainers = isAdvanced
                ? buildAdvancedExplainers(
                    plant,
                    disasters,
                    portfolio,
                    plantsById,
                    affectedPlantIdsByDisasterId
                  )
                : buildExplainers(
                    plant,
                    disasters,
                    portfolio,
                    plantsById,
                    affectedPlantIdsByDisasterId
                  );
              const firmTotal =
                result.firmCapacityTotalByType?.[plantId] ?? 0;
              const expectedFirmTotal =
                expectedFirmCapacityByType[plantId] ?? 0;
              return (
                <li key={plantId}>
                  <PlantPerformanceCard
                    plant={plant}
                    count={count}
                    expected={expected}
                    actual={actualSum}
                    mean={mean}
                    explainers={explainers}
                    formatActual={formatTwh}
                    expectedFirm={
                      isAdvanced ? expectedFirmTotal : undefined
                    }
                    actualFirm={isAdvanced ? firmTotal : undefined}
                    expectedFirmDisplay={
                      isAdvanced ? formatGw(expectedFirmTotal) : undefined
                    }
                    actualFirmDisplay={
                      isAdvanced ? formatGw(firmTotal) : undefined
                    }
                    testId={`grid-report-plant-${plantId}`}
                  />
                </li>
              );
            })}
          </ul>
        </section>

        {displayDisasters.length > 0 ? (
          <section
            className="grid-report-section grid-report-section-major grid-report-disruptions-section"
            aria-labelledby="grid-report-disasters-heading"
          >
            <h3
              id="grid-report-disasters-heading"
              className="grid-report-section-heading"
            >
              Disruption events and persistent effects
            </h3>
            <ul className="grid-report-disaster-list" role="list">
              {displayDisasters.map((disaster) => (
                <li key={disaster.id}>
                  <DisasterCard
                    disaster={disaster}
                    year={state.year}
                    advanced={isAdvanced}
                    noEffect={!disasterImpactById[disaster.id]}
                    testId={`grid-report-disaster-${disaster.id}`}
                  />
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        <div className="grid-report-actions">
          <Button
            onClick={handleAdvanceYear}
            changesScreen
            testId="year-summary-continue"
          >
            {continueLabel}
          </Button>
        </div>
      </div>
    </main>
  );
}

function formatEmissionsLabel(v) {
  if (!Number.isFinite(v)) return '';
  if (v > 0) return `+${v} tCO2e`;
  if (v < 0) return `${v} tCO2e`;
  return '0 tCO2e';
}
